<template>
  <div class="mini-overlay" tabindex="0" ref="root">
    <div class="mini-card">
      <div class="mini-header">
        <div>Mathe-Quiz — {{ score }} / {{ targetScore }}</div>
      </div>

      <div class="flag-area math-area">
        <div class="problem">{{ currentProblem.display }}</div>
        <div class="time-bar" aria-hidden="true">
          <div class="time-fill" :style="{ width: progress + '%' }"></div>
        </div>
      </div>

      <div class="options">
        <button v-for="(opt, i) in options" :key="i"
                :class="['opt-btn', { correct: feedback === 'correct' && opt === currentProblem.answer, wrong: feedback === 'wrong' && selected === opt }]" @click="choose(opt)">
          {{ opt }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MathQuizGame',
  emits: ['done','close'],
  props: {
    targetScore: { type: Number, default: 5 },
    timePerQuestion: { type: Number, default: 5 } // Sekunden
  },
  data() {
    return {
      score: 0,
      currentProblem: { display: '', answer: 0 },
      options: [],
      feedback: null,
      selected: null,
      busy: false,
      rafId: null,
      progress: 100 // Prozent, läuft rückwärts von 100 -> 0
    };
  },
  mounted() {
    this.nextRound();
    this.$refs.root && this.$refs.root.focus();
  },
  beforeUnmount() {
    this.clearTimer();
  },
  methods: {
    clearTimer() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    },
    startTimer() {
      this.clearTimer();
      const duration = this.timePerQuestion * 1000;
      const start = performance.now();
      const end = start + duration;
      const step = (now) => {
        const remaining = end - now;
        this.progress = Math.max(0, (remaining / duration) * 100);
        if (remaining <= 0) {
          this.clearTimer();
          this.onTimeout();
          return;
        }
        this.rafId = requestAnimationFrame(step);
      };
      // initialize and start
      this.progress = 100;
      this.rafId = requestAnimationFrame(step);
    },
    onTimeout() {
      this.feedback = 'wrong';
      this.selected = null;
      setTimeout(() => this.nextRound(), 350);
    },
    randInt(min, max) {
      return Math.floor(Math.random() * (max - min + 1)) + min;
    },
    generateProblem() {
      const ops = ['+', '-', '×', '÷'];
      const op = ops[Math.floor(Math.random() * ops.length)];

      let a, b, ans, display;

      if (op === '+') {
        a = this.randInt(50, 300);
        b = this.randInt(20, 200);
        ans = a + b;
        display = `${a} + ${b}`;
      } else if (op === '-') {
        a = this.randInt(100, 300);
        b = this.randInt(1, Math.min(200, a));
        ans = a - b;
        display = `${a} - ${b}`;
      } else if (op === '×') {
        a = this.randInt(6, 15);
        b = this.randInt(6, 15);
        ans = a * b;
        display = `${a} × ${b}`;
      } else { // ÷
        b = this.randInt(6, 20);
        ans = this.randInt(6, 30);
        a = ans * b;
        display = `${a} ÷ ${b}`;
      }

      return { display, answer: ans };
    },
    makeOptions(correct) {
      const opts = new Set();
      opts.add(correct);

      while (opts.size < 4) {
        let candidate;
        const absC = Math.abs(correct);

        if (Math.random() < 0.4) {
          const delta = this.randInt(1, Math.max(5, Math.round(absC * 0.05)));
          candidate = correct + (Math.random() < 0.5 ? -delta : delta);
        } else if (Math.random() < 0.8) {
          const delta = this.randInt(5, Math.max(10, Math.round(absC * 0.12)));
          candidate = correct + (Math.random() < 0.5 ? -delta : delta);
        } else {
          if (typeof correct === 'number' && correct > 10 && Math.random() < 0.6) {
            candidate = correct + this.randInt(-20, 20);
          } else {
            candidate = correct + this.randInt(-10, 10);
          }
        }
        if (candidate === correct) continue;
        opts.add(Math.round(candidate));
      }

      const arr = Array.from(opts);
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr.slice(0,4);
    },
    nextRound() {
      this.clearTimer();
      this.feedback = null;
      this.selected = null;
      this.busy = false;

      const prob = this.generateProblem();
      this.currentProblem = prob;
      this.options = this.makeOptions(prob.answer);
      if (!this.options.includes(prob.answer)) {
        this.options[0] = prob.answer;
      }
      this.startTimer();
    },
    choose(opt) {
      if (this.busy) return;
      this.busy = true;
      this.selected = opt;
      this.clearTimer();
      if (opt === this.currentProblem.answer) {
        this.feedback = 'correct';
        this.score++;
        if (this.score >= this.targetScore) {
          this.$emit('done', this.score);
          setTimeout(() => this.close(), 400);
          return;
        }
      } else {
        this.feedback = 'wrong';
      }
      setTimeout(() => this.nextRound(), 350);
    },
    close() {
      this.clearTimer();
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
.mini-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.5);
  z-index: 15000;
}
.mini-card {
  background: #fff;
  border-radius: 10px;
  padding: 12px;
  width: fit-content;
  max-width: 95%;
  box-shadow: 0 10px 30px rgba(0,0,0,0.4);
  display:flex;
  flex-direction: column;
  align-items: center;
}
.mini-header { width:100%; display:flex; justify-content:space-between; align-items:center; margin-bottom:8px; }
.math-area { width:320px; height:140px; display:flex; flex-direction:column; align-items:center; justify-content:center; background:#0f172a; border-radius:6px; margin-bottom:10px; padding:12px; box-sizing:border-box; }
.problem { font-size:2.2rem; color:#fff; font-weight:700; margin-bottom:12px; }
.time-bar { width:100%; height:10px; background: rgba(255,255,255,0.08); border-radius:6px; overflow:hidden; }
.time-fill { height:100%; width:100%; background: linear-gradient(90deg, #16a34a 0%, #f59e0b 55%, #ef4444 100%); transition: width 0.05s linear; }
.options { display:flex; flex-wrap:wrap; gap:8px; justify-content:center; margin-bottom:8px; }
.opt-btn { padding:8px 12px; border-radius:6px; border:0; background:#2d3748; color:#fff; cursor:pointer; min-width:140px; font-size:1rem; }
.opt-btn.correct { background:#16a34a; }
.opt-btn.wrong { background:#dc2626; opacity:0.95; }
.mini-close { background:transparent;border:0;cursor:pointer;font-size:16px }
.mini-footer { font-size:13px; color:#555; text-align:center; }
</style>